import {
  getOrganizationsSearchQueryKey,
  postOrganizationsByOrgIdEditMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, toast } from "@win-reality/react-ui";
import { z } from "zod";

import { AccountForm, accountFormSchema } from "./AccountForm";

export interface AccountEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: z.infer<typeof accountFormSchema>;
  organizationId: string;
}

export default function AccountEditModal({
  open,
  onOpenChange,
  defaultValues,
  organizationId,
}: AccountEditModalProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: editOrganizationMutation, isPending } = useMutation({
    ...postOrganizationsByOrgIdEditMutation(),
    onError: () => {
      toast("Failed to edit account", { variant: "error" });
    },
  });

  const handleSubmit = async (
    formData: z.infer<typeof accountFormSchema>,
    onSuccess: () => void,
  ) => {
    await editOrganizationMutation({
      body: {
        name: formData.accountName,
        city: formData.city,
        state: formData.state,
        sport: formData.sport,
        competitionLevel: formData.competitionLevel,
      },
      path: {
        org_id: encodeURIComponent(organizationId),
      },
    });
    queryClient.invalidateQueries({
      queryKey: getOrganizationsSearchQueryKey(),
    });
    onSuccess();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onOpenChange(false)}
      defaultCancelButton={false}
      size="lg"
    >
      {({ showDivider, setScrollContainer }) => (
        <AccountForm
          setScrollContainer={setScrollContainer}
          showDivider={showDivider}
          defaultValues={defaultValues}
          onSubmit={(data) => {
            handleSubmit(data, () => {
              onOpenChange(false);
            });
          }}
          mode="edit"
          organizationId={organizationId}
          isPending={isPending}
          onOpenChange={onOpenChange}
        />
      )}
    </Dialog>
  );
}
